import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * The New Renaissance e-book's own page — AI and legacy system modernization.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/ai-legacy-system-modernization/`), sent as five
 * screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam
 * şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's: the
 * two-column hero with the half-marble, half-circuitry bust beside it, the
 * centred standfirst under it, the grey "Why Download This Ebook?" card with the
 * pair of cover mockups, the "What You'll Learn" card with the six lessons, and
 * the centred "Start Your Legacy System Modernization Journey Today" ask. The
 * copy is that page's copy, verbatim.
 *
 * Seventh e-book page after Transform Better, Agentic AI, Sustainability
 * Innovation, Measurement, AI in Fashion Retail and Generative AI, and it
 * follows `ebook-generative-ai.ts` in every place the reference and this site
 * disagree — the same three deliberate departures, for the same reasons:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned "THE NEW RENAISSANCE" label. Every other
 *    hero here is that shape, and the words are unchanged.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book pages do. The card is A101's: of the twenty in
 *    `content/testimonials.ts` it is the one whose words are about modernizing
 *    an established business across *"customer experience, operational
 *    improvements, innovation, and digital culture"* and about doing it in
 *    consideration of corporate culture — which is this book's own three-part
 *    subject, systems, processes and culture. Its words are the Hall of Fame's;
 *    that file is where a change to them belongs.
 *
 * **One sentence moved inside the first card.** The reference sets "Don't just
 * adapt to the future—lead it." *under* the three-item list, and a card renders
 * heading → sub → body → points → button, so there is no slot after the list.
 * It leads the card instead, with "You'll discover:" introducing the list
 * immediately above it. Both sentences are the reference's, in the same card, in
 * the only order this template can set them.
 *
 * **The slug is confirmed, and it is not the one the catalogue derived.**
 * `content/ebooks.ts` guessed `the-new-renaissance` from the title; the
 * screenshots came from
 * `digitopia.co/impact-labs/ebooks/ai-legacy-system-modernization/` and carry
 * that address in their filenames, so the real slug is the descriptive one. The
 * catalogue entry was corrected to match, which also closed that e-book's slug
 * `check` — the fifth time the title rule has been caught out, after
 * Sustainability Innovation, Measurement, AI in Fashion Retail and Generative AI.
 */
export const route: RouteDef = {
  id: 'ebookLegacySystemModernization',
  path: 'impact-labs/ebooks/ai-legacy-system-modernization',
  priority: 0.7,
}

/**
 * The PDF, in the `website-documents` bucket — the same object the e-book
 * catalogue already points its card at. It is a local constant rather than a key
 * in `content/pages/links.ts`, because a page's own download is nobody else's
 * and five concurrent runs appending to that file is a push conflict.
 */
const EBOOK_PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + 'ec2a18db-5e37-491c-8e1a-dc6d20ec0dc9-Reinventing%20Legacy%20in%20the%20Age%20of%20AI.pdf'

export const ebookLegacySystemModernization: WrittenPage = {
  navLabel: 'The New Renaissance',
  title: 'Reinventing Legacy in the Age of AI',
  description:
    'Learn how AI drives legacy system modernization, transforms processes, and reshapes culture '
    + 'for business success in the Age of AI. Download The New Renaissance from Digitopia Impact '
    + 'Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'The New Renaissance',
      headline: 'Reinventing Legacy in the Age of AI',
      body: [
        'Learn how AI drives legacy system modernization, transforms processes, and reshapes '
          + 'culture for business success in the Age of AI.',
      ],
      ctas: [{ label: 'Download', href: EBOOK_PDF }],
      photo: {
        src: '/photo/legacy-modernization-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a marble classical bust whose right side dissolves into '
          + 'glowing blue circuitry, with figures drawn in orange light behind it.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take
      // the crown of the head and the plinth off it.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover artwork and the two mockups anybody has sent; every other hero on '
        + 'the site is 2560px, so this one is painted at roughly its own width on a '
        + 'laptop with nothing left over for a retina screen. If bigger exports '
        + 'exist wherever the covers were made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/legacy-modernization-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'prose',
      align: 'center',
      paragraphs: [
        'The Age of AI is here, and it’s redefining the way businesses operate. Legacy systems, '
        + 'outdated processes, and rigid management structures are holding companies back. This '
        + 'ebook is your guide to embracing a Renaissance mindset, one that empowers innovation, '
        + 'agility, and purpose-driven growth.',
      ],
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download This Ebook?',
      sub: 'Don’t just adapt to the future. Lead it.',
      body: ['You’ll discover:'],
      points: [
        'Why legacy systems are a liability in a world driven by intelligence.',
        'The principles that empower organizations to modernize and thrive.',
        'Real-world case studies that prove transformation is not just possible; it is essential.',
      ],
      ctas: [{ label: 'Download the eBook', href: EBOOK_PDF }],
      photo: {
        src: '/photo/legacy-modernization-ebook-covers.webp',
        alt:
          'Two copies of The New Renaissance, one behind the other, on a white background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      points: [
        'The Renaissance of Business: Explore why legacy systems need rethinking and how '
          + 'businesses can embrace the transformative power of AI.',
        'Redefining Leadership: Learn how modern management styles are crucial to fostering '
          + 'innovation and attracting top talent.',
        'Unlocking Hidden Assets: Discover why data is the lifeblood of the AI era and how to '
          + 'leverage it effectively.',
        'Rebuilding Processes for Agility: Transform outdated operations into adaptive, '
          + 'customer-centric models.',
        'Cultural Transformation: Understand how modernizing people and culture is at the heart '
          + 'of lasting change.',
        'Mastering Change Management: Gain actionable strategies to drive transformation with '
          + 'purpose and humanity.',
      ],
      ctas: [{ label: 'Download the eBook', href: EBOOK_PDF }],
      photo: {
        src: '/photo/legacy-modernization-ebook-stack.webp',
        alt: 'A stack of printed copies of The New Renaissance seen from above.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Start Your Legacy System Modernization Journey Today',
      sub:
        'Download your free copy of The New Renaissance: Reinventing Legacy in the Age of AI and '
        + 'discover how to modernize systems, processes, and culture.',
      cta: { label: 'Download the eBook', href: EBOOK_PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's A101 card, word for word — `content/testimonials.ts`
          // is where these words are edited, and the Turkish page carries its own
          // saying of them.
          quote:
            'We have been busy with digital transformation at A101 for the last 2 years focusing '
            + 'on the different dimensions of customer experience, operational improvements, '
            + 'innovation, and digital culture. Digitalisation is happening in all aspects of '
            + 'daily life. We wish to adapt to that and build a roadmap in consideration of '
            + 'country dynamics, retail sector dynamics and our corporate culture. One of the '
            + 'most important aspects of the process was the DMI Digital Maturity Index which '
            + 'allowed us to have a clear picture of where we are and to set our priorities in '
            + 'an objective manner.',
          source: 'Orhan Dağlıoğlugil, CTO',
          company: 'A101',
          logo: { name: 'A101', src: '/logo/a101card.png' },
          portrait: {
            src: '/photo/portrait-orhan-daglioglugil.png',
            alt: 'Orhan Dağlıoğlugil',
          },
        },
      ],
    },
  ],
}
